import { SitesActionTypes, SitesActionsUnion, UpdateSiteFailure } from '../actions/sites.actions';
import { Site, BoundingPolygon, FacilityHierarchyLevelName } from '../../sites';
import { getBoundaryColor, orderByProperty, addItemToTree, addItemToParentChildTree } from 'src/app/shared/utils';

interface SiteBoundary extends BoundingPolygon {
	isEditing: boolean;
}

export interface SitesState {
	sites: any[] | null;
	selectedSite: Site | null;
	selectedSiteTopLevelName: FacilityHierarchyLevelName | null;
	siteBoundaries: SiteBoundary[];
	hoveredSiteBoundary: string;
	drawerStatus: boolean;
	loading: boolean;
	loaded: boolean;
	saving: boolean;
	error: any;
}

const initialState: SitesState = {
	sites: null,
	selectedSite: null,
	selectedSiteTopLevelName: null,
	siteBoundaries: null,
	hoveredSiteBoundary: null,
	drawerStatus: false,
	loading: false,
	loaded: true,
	saving: false,
	error: null,
};

export function reducer(state: SitesState = initialState, action: SitesActionsUnion): SitesState {
	let sites;
	let selectedSite;
	let levels;
	let topLevelName;

	switch (action.type) {
		case SitesActionTypes.GetSite:
			return {
				...state,
				loading: true,
				loaded: false,
				drawerStatus: false,
			};

		case SitesActionTypes.GetSites:
			return {
				...state,
				loading: true,
				loaded: false,
			};

		
			case SitesActionTypes.GetSiteSuccess: {
			const payloadSite = action.payload;
			const polygons = payloadSite.boundingPolygons ?? [];

			const updatedPolygons = polygons.map((boundary, index) => {
				const colorIndex = index % 6;
				return {
				...boundary,
				color: getBoundaryColor(colorIndex),
				isEditing: false,
				currentBoundaryColorIndex: colorIndex,
				};
			});

			const updatedSite = {
				...payloadSite,
				boundingPolygons: updatedPolygons,
			};

			return {
				...state,
				loading: false,
				loaded: true,
				selectedSite: updatedSite,
			};
			}


		case SitesActionTypes.GetSitesSuccess:
			sites = action.payload;
			
			return {
				...state,
				loading: true,
				loaded: true,
				//sites: [...sites],
				sites: orderByProperty([...sites], 'name'),
			};

		// Add Site
		case SitesActionTypes.AddSite:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.AddSiteSuccess:
			const site = action.payload;
			// TODO: order alphabetically
			let sitesList = [...state.sites]
			sitesList.push(site);
			
			return {
				...state,
				sites: sitesList,
				selectedSite: site,
				saving: false,
			};
		case SitesActionTypes.AddSiteFailure:
			return {
				...state,
				saving: false,
				error: 'Failed to create site',
			};

		case SitesActionTypes.RemoveSite:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.RemoveSiteSuccess:
			const removeSite = action.payload;
			return {
				...state,
				saving: false,
			};
		case SitesActionTypes.RemoveSiteFailure:
			return {
				...state,
				saving: false,
				error: 'Failed to remove site',
			};

		// Update Site
		case SitesActionTypes.UpdateSite:
			return {
				...state,
				loading: true,
				loaded: false,
				saving: true,
				error: null,
			};

		
			case SitesActionTypes.UpdateSiteSuccess: {
			const payloadSite = action.payload;

			const polygons = payloadSite.boundingPolygons ?? [];

			const updatedPolygons = polygons.map((boundary, index) => {
				const colorIndex = index % 6;

				return {
				...boundary,
				color: getBoundaryColor(colorIndex),
				isEditing: false,
				currentBoundaryColorIndex: colorIndex,
				};
			});

			const updatedSite = {
				...payloadSite,
				boundingPolygons: updatedPolygons,
			};

			return {
				...state,
				loading: false,
				loaded: true,
				saving: false,
				selectedSite: updatedSite,
			};
			}

		// Failures
		case SitesActionTypes.GetSitesFailure:
		case SitesActionTypes.GetSiteFailure:
		case SitesActionTypes.UpdateSiteFailure:
			return {
				...state,
				error: null,
			};

		case SitesActionTypes.OpenDrawer:
			return {
				...state,
				drawerStatus: true,
			};

		case SitesActionTypes.CloseDrawer:
			return {
				...state,
				drawerStatus: false,
			};

		case SitesActionTypes.HoverSiteBoundary: {
			return {
				...state,
				hoveredSiteBoundary: action.boundaryId,
			};
		}

		case SitesActionTypes.ClearSiteBoundaryHover: {
			return {
				...state,
				hoveredSiteBoundary: null,
			};
		}

		
			case SitesActionTypes.RemoveBoundary: {
			const site = action.site;
			const boundaryToRemove = action.boundary;

			const polygons = site.boundingPolygons ?? [];

			const updatedPolygons = polygons.filter(p => p.id !== boundaryToRemove.id);

			const updatedSite = {
				...site,
				boundingPolygons: updatedPolygons,
			};

			return {
				...state,
				selectedSite: updatedSite,
			};
			}


		case SitesActionTypes.AddFacilityHierarchyLevel:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.AddFacilityHierarchyLevelSuccess:
			if (!action.facilityHierarchyLevel.parentId) {
				levels = [...state.selectedSite.facilityHierarchyLevels, action.facilityHierarchyLevel];
			} else {
				levels = addItemToTree(state.selectedSite.facilityHierarchyLevels, action.facilityHierarchyLevel);
			}
			let selectedSite = {...state.selectedSite};
			selectedSite.facilityHierarchyLevels = levels;
			return {
				...state,
				selectedSite: selectedSite,
				saving: false,
			};
		case SitesActionTypes.AddFacilityHierarchyLevelFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		case SitesActionTypes.UpdateFacilityHierarchyLevel:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.UpdateFacilityHierarchyLevelSuccess:
			if (!action.facilityHierarchyLevel.parentId) {
				levels = [...state.selectedSite.facilityHierarchyLevels, action.facilityHierarchyLevel];
			} else {
				levels = addItemToTree(state.selectedSite.facilityHierarchyLevels, action.facilityHierarchyLevel);
			}

			selectedSite = state.selectedSite;
			selectedSite.facilityHierarchyLevels = levels;
			return {
				...state,
				selectedSite: selectedSite,
				saving: false,
			};

		case SitesActionTypes.UpdateFacilityHierarchyLevelFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		case SitesActionTypes.DeleteFacilityHierarchyLevel:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.DeleteFacilityHierarchyLevelSuccess:
			return {
				...state,
				saving: false,
			};
		case SitesActionTypes.DeleteFacilityHierarchyLevelFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		case SitesActionTypes.AddFacilityHierarchyLevelName:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.AddFacilityHierarchyLevelNameSuccess:
			if (!action.facilityHierarchyLevelName.parentId) {
				topLevelName = action.facilityHierarchyLevelName;
			} else {
				topLevelName = addItemToParentChildTree(state.selectedSiteTopLevelName, action.facilityHierarchyLevelName);
			}

			return {
				...state,
				selectedSiteTopLevelName: topLevelName,
				saving: false,
			};

		case SitesActionTypes.AddFacilityHierarchyLevelNameFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		case SitesActionTypes.UpdateFacilityHierarchyLevelName:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.UpdateFacilityHierarchyLevelNameSuccess:
			if (!action.facilityHierarchyLevelName.parentId) {
				topLevelName = action.facilityHierarchyLevelName;
			} else {
				topLevelName = addItemToParentChildTree(state.selectedSiteTopLevelName, action.facilityHierarchyLevelName);
			}

			return {
				...state,
				selectedSiteTopLevelName: topLevelName,
				saving: false,
			};

		case SitesActionTypes.UpdateFacilityHierarchyLevelNameFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		case SitesActionTypes.DeleteFacilityHierarchyLevelName:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.DeleteFacilityHierarchyLevelNameSuccess:
			return {
				...state,
				saving: false,
			};
		case SitesActionTypes.DeleteFacilityHierarchyLevelNameFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		case SitesActionTypes.GetFacilityHierarchyLevelNamesForSite:
			return {
				...state,
				saving: true,
				error: null,
			};

		case SitesActionTypes.GetFacilityHierarchyLevelNamesForSiteSuccess:
			topLevelName = action.topLevelName instanceof Array ? action.topLevelName[0] : null;
			return {
				...state,
				selectedSiteTopLevelName: topLevelName,
				saving: false,
			};

		case SitesActionTypes.GetFacilityHierarchyLevelNamesForSiteFailure:
			return {
				...state,
				saving: false,
				error: action.error,
			};

		// default
		default:
			return state;
	}
}

export const getSites = (state: SitesState) => state.sites;

export const getLoading = (state: SitesState) => state.loading;

export const getLoaded = (state: SitesState) => state.loaded;

export const getSitesError = (state: SitesState) => state.error;

export const getSite = (state: SitesState) => state.selectedSite;

export const getSelectedSiteTopLevelName = (state: SitesState) => state.selectedSiteTopLevelName;

export const getSiteError = (state: SitesState) => state.error;

export const getDrawerStatus = (state: SitesState) => state.drawerStatus;

export const getHoveredBoundary = (state: SitesState) => state.hoveredSiteBoundary;
